import { Box, Typography, Paper, Stack, TextField, Chip } from '@mui/material';
import Link from 'next/link';
import { FC, useMemo, useState } from 'react';
import { Footer } from './Footer';
import DetailField from '@/components/common/DetailField';
import BasicActivityInfo from '@/components/details/BasicActivityInfo';

import { PrivilegeEnum, SampleActivityDto, StatusDto, UpdateSampleActivityRequest } from '@/api/ApiClient';
import { useBackendStore } from '@/stores/backendStore';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useSampleActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { log } from '@/lib/utils/logger';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { updateSampleActivityWithPreconditionRetry } from '@/lib/tree/submitEntityUtils';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createSampleActivityBasicSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useTreeStore } from '@/stores/treeStore';
import { buildProjectSampleHref } from '@/lib/routing/appPaths';

/**
 * Props for the TreeArea component
 */
export const selectOptions: OptionKey[] = ['researcherOptions', 'facultyOptions', 'status', 'visibility'];

/** Renders a project chip; links to the sample in that project unless it is the current project tree. */
function TransferProjectChip({
  label,
  sampleLabel,
  currentProjectLabel,
  basePath,
}: {
  label: string;
  sampleLabel: string | undefined;
  currentProjectLabel: string | undefined;
  basePath: string | undefined;
}) {
  const href =
    sampleLabel && currentProjectLabel !== label ? buildProjectSampleHref(basePath, label, sampleLabel) : undefined;

  if (!href) {
    return <Chip label={label} size="small" />;
  }

  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
      <Chip label={label} size="small" clickable component="span" />
    </Link>
  );
}

const TransferActivityDetails: FC = () => {
  const backend = useBackendStore.getState().getBackend();
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { resolveUser } = useUserResolution();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();

  const { setDataModified, updateDataPartial, updateData, getSampleData, getProjectData } = useTree();
  const { selectedNode } = useTreeSelection();
  const nodes = useTreeStore((state) => state.nodes);
  const systemConfig = useSystemConfig();

  // Check if this is a readonly node type
  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  const [isReadOnly, setIsReadOnly] = useState(true);

  const activity = useSampleActivityData(selectedNode);

  const sampleLabel = useMemo(() => {
    if (activity?.sample?.sampleLabel) {
      return activity.sample.sampleLabel;
    }
    if (!selectedNode?.parentId) {
      return undefined;
    }
    let currentId: string | undefined = selectedNode.parentId;
    for (let depth = 0; depth < 16 && currentId; depth++) {
      const node = nodes.get(currentId);
      if (!node) break;
      if (
        node.type === 'sample' ||
        node.type === 'sampleAggregate' ||
        node.type === 'sampleInPackage' ||
        node.type === 'sampleSnapshot'
      ) {
        const sample = getSampleData(node);
        return sample?.sampleLabel || (sample?.id != null ? String(sample.id) : undefined);
      }
      currentId = node.parentId;
    }
    return undefined;
  }, [activity, getSampleData, nodes, selectedNode]);

  const currentProjectLabel = useMemo(() => {
    if (!selectedNode?.parentId) {
      return undefined;
    }
    let currentId: string | undefined = selectedNode.parentId;
    for (let depth = 0; depth < 16 && currentId; depth++) {
      const node = nodes.get(currentId);
      if (!node) break;
      if (node.type === 'project' || node.type === 'projectInData') {
        return getProjectData(node)?.projectLabel;
      }
      currentId = node.parentId;
    }
    return undefined;
  }, [getProjectData, nodes, selectedNode]);

  // Centralized date handling utility
  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? new Date(date.toDateString()) : undefined;
  };

  if (!activity || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!activity ? 'Select a node to view details' : 'Activity not found.'}
        </Typography>
      </Box>
    );
  }

  const isSubmitted = activity?.status === StatusDto.Submitted;

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }
  const handleModification = (modified: Partial<SampleActivityDto>) => {
    updateDataPartial(selectedNode.dataId, modified);
  };

  const handleSave = async () => {
    if (!activity.id) return;
    const saveValidation = validateRequiredToSave(createSampleActivityBasicSaveValidation(activity));
    if (!saveValidation.isValid) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    try {
      const request: UpdateSampleActivityRequest = { ...activity };
      const preconditionDeps = { showSnackbar, showConfirmDialog };
      const updated = await updateSampleActivityWithPreconditionRetry(backend, activity.id, request, preconditionDeps);
      if (updated === null) return;
      // Update the data in the store
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, false);
      setIsReadOnly(true);
      showSnackbar('Transfer activity saved successfully', 'success');
    } catch (error) {
      log.error('[TransferActivity] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save transfer activity.', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <BasicActivityInfo
            activity={activity}
            isReadOnly={isReadOnly}
            isSubmitted={isSubmitted}
            handleModification={handleModification}
            convertDateForBackend={convertDateForBackend}
            hideVisibility={isReadOnlyNode}
          />
          <Typography variant="h6" gutterBottom>
            Project Information
          </Typography>

          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                <Typography variant="subtitle2" color="text.secondary">
                  From Project(s)
                </Typography>
                {activity.sourceProjectLabels?.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {activity.sourceProjectLabels.map((label, index) => (
                      <TransferProjectChip
                        key={index}
                        label={label}
                        sampleLabel={sampleLabel}
                        currentProjectLabel={currentProjectLabel}
                        basePath={systemConfig?.basePath}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body1">None</Typography>
                )}
              </Box>

              {/* To Project */}
              <Box sx={{ width: { xs: '100%', sm: '48%' } }}>
                <Typography variant="subtitle2" color="text.secondary">
                  To Project(s)
                </Typography>
                {activity.targetProjectLabels?.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {activity.targetProjectLabels.map((label, index) => (
                      <TransferProjectChip
                        key={index}
                        label={label}
                        sampleLabel={sampleLabel}
                        currentProjectLabel={currentProjectLabel}
                        basePath={systemConfig?.basePath}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body1">None</Typography>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Description */}
          {activity.desc || !isReadOnly || isSubmitted ? (
            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
              <DetailField label="Description" value={activity.desc} isReadOnly={isReadOnly} width={{ width: '100%' }}>
                <TextField
                  value={activity.desc || ''}
                  onChange={(e) =>
                    handleModification({
                      desc: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  slotProps={{
                    input: { readOnly: isReadOnly },
                  }}
                />
              </DetailField>
            </Paper>
          ) : null}
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        {isReadOnlyNode ? (
          <ReadOnlyFooter
            lastChangedDate={activity.lastChangedDate}
            lastChangedBy={activity.lastChangedByUser || 'Unknown'}
            status={activity.status || 'Draft'}
          />
        ) : (
          <Footer
            lastChangedDate={activity.lastChangedDate}
            lastChangedBy={resolveUser(activity.lastChangedByUser || '')}
            status={activity.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={setIsReadOnly}
            onSave={handleSave}
            privilege={PrivilegeEnum.TransferActivity}
          />
        )}
      </Box>
    </Box>
  );
};

export default TransferActivityDetails;
